import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Modal, Spinner } from "react-bootstrap";
import { FiArchive, FiCalendar, FiMapPin, FiPlus, FiRotateCcw, FiSearch, FiUsers } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import IniciativaView from "../../components/1.7 iniciativas/Iniciativa";
import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";
import "./IniciativasPage.css";

const formatFilters = [
  ["todas", "Todas"],
  ["presencial", "Presencial"],
  ["online", "Online"],
  ["hibrido", "Híbrido"],
];

const productKey = (item) => (item?.tipo?.nombre || "").toLocaleLowerCase("es");
const normalizedKey = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("es");
const formatKey = (item) => {
  const value = normalizedKey(item?.formato);
  if (value.includes("presencial")) return "presencial";
  if (value.includes("hibrid")) return "hibrido";
  if (value.includes("online") || value.includes("linea") || value.includes("virtual")) return "online";
  return "";
};
const toIsoDate = (value) => {
  if (!value) return null;
  const parts = String(value).split("-");
  return parts[0]?.length === 4 ? value : parts.reverse().join("-");
};
const toTableItem = (item) => {
  const participations = item.participaciones || [];
  return {
    ...item,
    empresa: Array.isArray(item.empresa)
      ? item.empresa.map((company) => company.nombre_completo || company.nombre).filter(Boolean).join(", ")
      : item.empresa,
    f_inicio_iso: item.f_inicio_iso || toIsoDate(item.f_inicio),
    f_termino: toIsoDate(item.f_termino),
    elegidas_count: item.elegidas_count ?? participations.filter((entry) => entry.inscrita).length,
    aprobadas_count: item.aprobadas_count ?? participations.filter((entry) => entry.inscrita && entry.aprobada).length,
  };
};
const temporalStatus = (item) => {
  if (!item?.f_inicio_iso) return "past";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(`${item.f_inicio_iso}T00:00:00`);
  const end = new Date(`${item.f_termino || item.f_inicio_iso}T00:00:00`);
  if (start > today) return "upcoming";
  if (start <= today && end >= today) return "current";
  return "past";
};

const IniciativasPage = ({ programMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [formatFilter, setFormatFilter] = useState("todas");
  const [productOptions, setProductOptions] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [inactiveRecords, setInactiveRecords] = useState(null);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [reactivationTypes, setReactivationTypes] = useState({});
  const [reactivating, setReactivating] = useState(null);
  const [reactivationError, setReactivationError] = useState("");
  const basePath = programMode ? "/programas" : "/iniciativas";
  const listPath = basePath;
  const pageTitle = programMode ? "Programas" : "Iniciativas";
  const listScope = programMode ? "programas" : "iniciativas";
  const matchesPageType = (item) => {
    const key = productKey(item);
    return programMode ? key === "programa" : !["programa", "suspendida"].includes(key);
  };
  const scopedItems = useMemo(
    () => items.filter((item) => item.activa !== false && productKey(item) !== "suspendida" && matchesPageType(item)),
    // matchesPageType depends only on these values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, programMode],
  );
  const inactiveItems = useMemo(
    () => (inactiveRecords || []).filter((item) => {
      if (item.activa !== false && productKey(item) !== "suspendida") return false;
      if (productKey(item) === "suspendida") return !programMode;
      return matchesPageType(item);
    }),
    // matchesPageType depends only on these values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inactiveRecords, programMode],
  );
  const reactivationProducts = useMemo(
    () => productOptions.filter((item) => item.activo && item.nombre.toLocaleLowerCase("es") !== "suspendida"),
    [productOptions],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setItems([]);
    setInactiveRecords(null);
    fetch(getApiUrl(`api/iniciativas/tabla/?scope=${listScope}&activa=true`), { credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error("No se pudieron cargar las iniciativas.");
        return response.json();
      })
      .then((data) => { if (active) setItems(data); })
      .catch((error) => console.error(error))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [listScope]);

  const openInactiveItems = async () => {
    setReactivationError("");
    setShowInactive(true);
    if (inactiveRecords !== null || inactiveLoading) return;
    setInactiveLoading(true);
    try {
      const [tableResponse, optionsResponse] = await Promise.all([
        fetch(getApiUrl(`api/iniciativas/tabla/?scope=${listScope}&activa=false`), { credentials: "include" }),
        fetch(getApiUrl("api/iniciativas-opciones/?only=productos"), { credentials: "include" }),
      ]);
      if (!tableResponse.ok || !optionsResponse.ok) throw new Error("No se pudieron cargar los registros inactivos.");
      const [records, options] = await Promise.all([tableResponse.json(), optionsResponse.json()]);
      setInactiveRecords(records);
      setProductOptions(options.productos || []);
    } catch (error) {
      setReactivationError(error.message);
      setInactiveRecords(null);
    } finally {
      setInactiveLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    if (!id) {
      setSelected(null);
      setDetailBusy(false);
      return () => { active = false; };
    }
    setDetailBusy(false);
    setDetailLoading(true);
    fetch(getApiUrl(`api/iniciativas/${id}/`), { credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error("No se pudo cargar la iniciativa.");
        return response.json();
      })
      .then((data) => { if (active) setSelected(data); })
      .catch((error) => console.error(error))
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [id]);

  const handleInitiativeUpdated = useCallback((updated) => {
    const tableUpdate = toTableItem(updated);
    setSelected((current) => current ? { ...current, ...updated } : updated);
    setItems((current) => current.map((item) => (
      item.id === updated.id ? { ...item, ...tableUpdate } : item
    )));
    setInactiveRecords((current) => {
      if (!Array.isArray(current)) return current;
      if (updated.activa !== false && productKey(updated) !== "suspendida") {
        return current.filter((item) => item.id !== updated.id);
      }
      const exists = current.some((item) => item.id === updated.id);
      return exists
        ? current.map((item) => item.id === updated.id ? { ...item, ...tableUpdate } : item)
        : [tableUpdate, ...current];
    });
  }, []);

  const closeDetail = () => {
    if (!detailBusy) navigate(listPath);
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("es");
    return scopedItems.filter((item) => {
      if (formatFilter !== "todas" && formatKey(item) !== formatFilter) return false;
      if (!normalized) return true;
      return [item.nombre, item.tipo?.nombre, item.comuna, item.lugar, item.formato, item.oficio, item.empresa, item.etiqueta]
        .filter(Boolean)
        .some((value) => value.toLocaleLowerCase("es").includes(normalized));
    });
  }, [scopedItems, query, formatFilter]);

  const reactivate = async (item) => {
    const tipoId = reactivationTypes[item.id];
    const needsType = productKey(item) === "suspendida";
    if (needsType && !tipoId) return;
    setReactivating(item.id);
    setReactivationError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/iniciativas/${item.id}/reactivar/`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify(needsType ? { tipo_id: Number(tipoId) } : {}),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "No se pudo reactivar la iniciativa.");
      const selectedType = needsType
        ? reactivationProducts.find((type) => type.id === Number(tipoId))
        : item.tipo;
      const reactivatedItem = { ...item, tipo: selectedType, activa: true };
      setInactiveRecords((current) => Array.isArray(current)
        ? current.filter((currentItem) => currentItem.id !== item.id)
        : current);
      if (matchesPageType(reactivatedItem)) {
        setItems((current) => current.some((currentItem) => currentItem.id === item.id)
          ? current.map((currentItem) => currentItem.id === item.id ? reactivatedItem : currentItem)
          : [reactivatedItem, ...current]);
      }
      setReactivationTypes((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
    } catch (error) {
      setReactivationError(error.message);
    } finally {
      setReactivating(null);
    }
  };

  return (
    <div className="initiatives-page">
      <section className="initiatives-hero">
        <div>
          <span className="initiatives-eyebrow">Coordinación Interna</span>
          <h1>{pageTitle}</h1>
          <p>{programMode ? "Programas formativos de RedMaestra." : "Listado de eventos celebrados por RedMaestra."}</p>
        </div>
        <div className="initiatives-hero-actions">
          <button className="initiatives-secondary-action" onClick={openInactiveItems}>
            <FiArchive /> Inactivas {Array.isArray(inactiveRecords) && <span>{inactiveItems.length}</span>}
          </button>
          <button className="initiatives-primary-action" onClick={() => navigate(programMode ? "/iniciativas/nueva?tipo=programa" : "/iniciativas/nueva")}>
            <FiPlus /> {programMode ? "Nuevo programa" : "Nueva iniciativa"}
          </button>
        </div>
      </section>

      <section className="initiatives-toolbar">
        <label className="initiatives-search">
          <FiSearch />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por nombre, comuna, oficio o empresa..." />
        </label>
        <output className="initiatives-result-count" aria-live="polite" aria-label={`${filtered.length} ${programMode ? "programas encontrados" : "iniciativas encontradas"}`}>
          {filtered.length}
        </output>
        <div className="initiatives-filter" role="group" aria-label="Filtrar por formato">
          {formatFilters.map(([value, label]) => (
            <button key={value} className={formatFilter === value ? "active" : ""} onClick={() => setFormatFilter(value)}>{label}</button>
          ))}
        </div>
      </section>

      {loading ? (
        <div className="initiatives-state"><Spinner animation="border" /><span>Cargando iniciativas...</span></div>
      ) : filtered.length === 0 ? (
        <div className="initiatives-state"><h3>No encontramos {programMode ? "programas" : "iniciativas"}</h3><p>Prueba con otra búsqueda o filtro.</p></div>
      ) : (
        <section className="initiatives-grid">
          {filtered.map((item) => {
            const percentage = item.elegidas_count ? Math.round((item.aprobadas_count / item.elegidas_count) * 100) : 0;
            const status = temporalStatus(item);
            const pendingOutcome = status === "upcoming" || status === "current";
            return (
              <article className={`initiative-card initiative-card-${status}`} key={item.id} tabIndex="0" onClick={() => navigate(`${basePath}/${item.id}`)} onKeyDown={(event) => { if (event.key === "Enter") navigate(`${basePath}/${item.id}`); }}>
                <div className="initiative-card-topline">
                  <span className={`initiative-type initiative-type-${productKey(item) || "product"}`}>{item.tipo?.nombre || "Sin tipo"}</span>
                  <div className="initiative-card-timing">
                    {status === "current" && <span className="initiative-status-label current">En ejecución</span>}
                    <span className="initiative-date"><FiCalendar /> {item.f_inicio}</span>
                  </div>
                </div>
                <h2>{item.nombre}</h2>
                <div className="initiative-tags">
                  {item.empresa && <span>{item.empresa}</span>}
                  {item.oficio && <span>{item.oficio}</span>}
                  {item.etiqueta && <span>{item.etiqueta}</span>}
                </div>
                <div className="initiative-meta">
                  <span><FiMapPin /> {item.comuna || "Sin comuna"}</span>
                  <span><FiUsers /> {item.elegidas_count} participantes</span>
                </div>
                {!pendingOutcome && (
                  <>
                    <div className="initiative-progress-copy"><span>{item.aprobadas_count} aprobadas</span><strong>{percentage}%</strong></div>
                    <div className="initiative-progress"><span style={{ width: `${percentage}%` }} /></div>
                  </>
                )}
                {status === "upcoming" && <span className="initiative-upcoming-ribbon">Próximamente</span>}
              </article>
            );
          })}
        </section>
      )}

      {detailLoading && <div className="initiatives-detail-loading"><Spinner animation="border" /></div>}
      <Modal show={Boolean(selected)} onHide={closeDetail} backdrop="static" keyboard={!detailBusy} dialogClassName="initiative-detail-modal">
        <Modal.Header closeButton={!detailBusy} className="initiative-modal-header" aria-label={`Cerrar detalle de ${programMode ? "programa" : "iniciativa"}`} />
        <Modal.Body className="initiative-modal-body">
          {selected && (
            <IniciativaView
              iniciativa={selected}
              onIniciativaUpdated={handleInitiativeUpdated}
              onBusyChange={setDetailBusy}
            />
          )}
        </Modal.Body>
      </Modal>
      <Modal show={showInactive} onHide={() => setShowInactive(false)} centered size="lg" dialogClassName="suspended-initiatives-modal">
        <Modal.Header closeButton>
          <Modal.Title>{programMode ? "Programas inactivos" : "Iniciativas inactivas"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="suspended-modal-copy">Reactiva el registro cuando deba volver a los listados. Solo los registros antiguos sin clasificación válida necesitan que selecciones una.</p>
          {reactivationError && <Alert variant="danger">{reactivationError}</Alert>}
          {inactiveLoading ? (
            <div className="suspended-empty"><Spinner animation="border" /><span>Cargando registros inactivos...</span></div>
          ) : inactiveItems.length === 0 ? (
            <div className="suspended-empty"><FiArchive /><span>No hay {programMode ? "programas inactivos" : "iniciativas inactivas"}.</span></div>
          ) : (
            <div className="suspended-list">
              {inactiveItems.map((item) => (
                <article className="suspended-item" key={item.id}>
                  <div className="suspended-item-copy">
                    <strong>{item.nombre}</strong>
                    <span>{item.f_inicio} · {item.comuna || "Sin comuna"}</span>
                  </div>
                  {productKey(item) === "suspendida" ? <label>
                    <span className="visually-hidden">Nuevo tipo para {item.nombre}</span>
                    <select value={reactivationTypes[item.id] || ""} onChange={(event) => setReactivationTypes((current) => ({ ...current, [item.id]: event.target.value }))}>
                      <option value="">Elegir nuevo tipo</option>
                      {reactivationProducts.map((type) => <option key={type.id} value={type.id}>{type.nombre}</option>)}
                    </select>
                  </label> : <span className={`initiative-type initiative-type-${productKey(item)}`}>{item.tipo?.nombre}</span>}
                  <button type="button" disabled={(productKey(item) === "suspendida" && !reactivationTypes[item.id]) || reactivating === item.id} onClick={() => reactivate(item)}>
                    {reactivating === item.id ? <Spinner animation="border" size="sm" /> : <FiRotateCcw />} Reactivar
                  </button>
                </article>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default IniciativasPage;
